export class SoundManager {
  private static bgMusic: HTMLAudioElement | null = null;
  private static soundEnabled: boolean = true;
  private static musicEnabled: boolean = true;

  /**
   * Initialize sound manager
   */
  static init() {
    // Load settings from localStorage
    const soundSetting = localStorage.getItem('instantale_sound_enabled');
    const musicSetting = localStorage.getItem('instantale_music_enabled');

    this.soundEnabled = soundSetting !== 'false';
    this.musicEnabled = musicSetting !== 'false';
  }

  /**
   * Play background music
   */
  static playBackgroundMusic(theme: string = 'default') {
    if (!this.musicEnabled) return;

    // For now, we'll use a simple tone generation
    // In production, you'd load actual music files
    if (!this.bgMusic) {
      // Create a simple ambient tone using Web Audio API
      this.createAmbientMusic(theme);
    }
  }

  /**
   * Stop background music
   */
  static stopBackgroundMusic() {
    if (this.bgMusic) {
      this.bgMusic.pause();
      this.bgMusic = null;
    }
  }

  /**
   * Play button click sound
   */
  static playClickSound() {
    if (!this.soundEnabled) return;
    this.playTone(800, 0.1, 0.05);
  }

  /**
   * Play choice selection sound
   */
  static playChoiceSound(risk?: 'high' | 'medium' | 'low') {
    if (!this.soundEnabled) return;

    switch (risk) {
      case 'high':
        this.playTone(400, 0.2, 0.1); // Low warning tone
        break;
      case 'medium':
        this.playTone(600, 0.15, 0.08);
        break;
      default:
        this.playTone(900, 0.1, 0.06);
    }
  }

  /**
   * Play success sound
   */
  static playSuccessSound() {
    if (!this.soundEnabled) return;
    this.playTone(1200, 0.15, 0.1);
    setTimeout(() => this.playTone(1600, 0.1, 0.08), 100);
  }

  /**
   * Play error sound
   */
  static playErrorSound() {
    if (!this.soundEnabled) return;
    this.playTone(300, 0.3, 0.15);
  }

  /**
   * Toggle sound effects
   */
  static toggleSound(): boolean {
    this.soundEnabled = !this.soundEnabled;
    localStorage.setItem('instantale_sound_enabled', String(this.soundEnabled));
    return this.soundEnabled;
  }

  /**
   * Toggle background music
   */
  static toggleMusic(): boolean {
    this.musicEnabled = !this.musicEnabled;
    localStorage.setItem('instantale_music_enabled', String(this.musicEnabled));

    if (!this.musicEnabled) {
      this.stopBackgroundMusic();
    }

    return this.musicEnabled;
  }

  /**
   * Get sound enabled status
   */
  static isSoundEnabled(): boolean {
    return this.soundEnabled;
  }

  /**
   * Get music enabled status
   */
  static isMusicEnabled(): boolean {
    return this.musicEnabled;
  }

  /**
   * Play a simple tone
   */
  private static playTone(frequency: number, duration: number, volume: number = 0.1) {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      gainNode.gain.value = volume;

      oscillator.start();
      oscillator.stop(audioContext.currentTime + duration);
    } catch (error) {
      console.warn('[SoundManager] Audio playback failed:', error);
    }
  }

  /**
   * Create ambient background music
   */
  private static createAmbientMusic(theme: string) {
    // Simple ambient music generation
    // In production, you'd use actual audio files
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

      // Create a simple drone note based on theme
      const baseFrequency = this.getThemeFrequency(theme);

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = baseFrequency;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.02; // Very quiet ambient

      oscillator.start();

      // Store reference (simplified for demo)
      console.log('[SoundManager] Ambient music playing');
    } catch (error) {
      console.warn('[SoundManager] Background music failed:', error);
    }
  }

  /**
   * Get base frequency for theme
   */
  private static getThemeFrequency(theme: string): number {
    const frequencies: { [key: string]: number } = {
      'dark-fantasy': 110,
      'horror': 90,
      'cyberpunk': 130,
      'steampunk': 120,
      'default': 100
    };
    return frequencies[theme] || frequencies['default'];
  }
}
